import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  TextInput,
  ActivityIndicator,
  Alert,
  Dimensions,
  Switch,
  Platform,
  Image,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

const { width, height } = Dimensions.get('window');

const API_BASE_URL = 'https://api.jobeng.ai';
const API_ENDPOINTS = {
  companyProfile: `${API_BASE_URL}/api/data/company_profiles`,
  jobs: `${API_BASE_URL}/api/data/jobs`,
};

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
  },
});

api.interceptors.request.use(
  async (config) => {
    try {
      const token = await AsyncStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      const companyId = await AsyncStorage.getItem('company_id');
      if (companyId) {
        config.params = { ...config.params, company_id: parseInt(companyId) };
      }
    } catch (error) {
      console.error('Error in request interceptor:', error);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export default function CreateJobScreen({ navigation, route }) {
  const { job, mode, template } = route?.params || {};
  const isEditMode = mode === 'edit';
  const isDuplicateMode = mode === 'duplicate';
  const isTemplateMode = !!template;
  
  // Form State
  const [isLoading, setIsLoading] = useState(false);
  const [companyLogo, setCompanyLogo] = useState(route?.params?.companyLogo || null);
  const [companyName, setCompanyName] = useState(route?.params?.companyName || '');
  const [isSaving, setIsSaving] = useState(false);
  
  // Job Details
  const [jobTitle, setJobTitle] = useState(template?.title || job?.title || '');
  const [department, setDepartment] = useState(template?.customFields?.department || job?.department || '');
  const [location, setLocation] = useState(template?.customFields?.location || job?.location || '');
  const [jobType, setJobType] = useState(template?.type || job?.jobType || 'Full-time');
  const [workMode, setWorkMode] = useState(template?.customFields?.workMode || job?.workMode || 'On-site');
  
  // Job Description
  const [description, setDescription] = useState(template?.description || job?.description || '');
  const [responsibilities, setResponsibilities] = useState(
    template?.responsibilities ? (Array.isArray(template.responsibilities) ? template.responsibilities.join('\n') : template.responsibilities) 
    : job?.responsibilities ? (Array.isArray(job.responsibilities) ? job.responsibilities.join('\n') : job.responsibilities) : ''
  );
  const [requirements, setRequirements] = useState(
    template?.requirements ? (Array.isArray(template.requirements) ? template.requirements.join('\n') : template.requirements)
    : job?.requirements ? (Array.isArray(job.requirements) ? job.requirements.join('\n') : job.requirements)
    : job?.qualifications ? (Array.isArray(job.qualifications) ? job.qualifications.join('\n') : job.qualifications) : ''
  );
  const [benefits, setBenefits] = useState(
    template?.benefits ? (Array.isArray(template.benefits) ? template.benefits.join('\n') : template.benefits)
    : job?.benefits ? (typeof job.benefits === 'string' ? job.benefits : Array.isArray(job.benefits) ? job.benefits.join('\n') : JSON.stringify(job.benefits)) : ''
  );
  
  // Compensation
  const [salaryMin, setSalaryMin] = useState(
    template?.customFields?.salaryRange?.min?.toString() || job?.salaryMin?.toString() || job?.salary_min?.toString() || ''
  );
  const [salaryMax, setSalaryMax] = useState(
    template?.customFields?.salaryRange?.max?.toString() || job?.salaryMax?.toString() || job?.salary_max?.toString() || ''
  );
  const [currency, setCurrency] = useState(
    template?.customFields?.salaryRange?.currency || job?.currency || job?.salary_currency || 'GBP'
  );
  
  // Settings
  const [deadline, setDeadline] = useState(job?.deadline || job?.application_deadline || '');
  const [positions, setPositions] = useState(
    template?.customFields?.positions?.toString() || job?.positions?.toString() || job?.positions_available?.toString() || '1'
  );
  const [experienceLevel, setExperienceLevel] = useState(
    template?.level || job?.experienceLevel || job?.experience_level || 'Mid-level'
  );
  const [isFeatured, setIsFeatured] = useState(job?.isFeatured || job?.is_featured || false);
  const [aiPowered, setAiPowered] = useState(job?.aiPowered || true);
  const [status, setStatus] = useState(job?.status || 'draft');
  const [requiredSkills, setRequiredSkills] = useState(
    template?.tags || job?.requiredSkills || job?.required_skills || job?.skills || []
  );

  const jobTypes = ['Full-time', 'Part-time', 'Contract', 'Temporary', 'Internship'];
  const workModes = ['On-site', 'Remote', 'Hybrid'];
  const experienceLevels = ['Entry-level', 'Mid-level', 'Senior', 'Lead', 'Executive'];
  const currencies = ['GBP', 'USD', 'EUR', 'SAR', 'AED'];

  useEffect(() => {
    if (isDuplicateMode && job) {
      setJobTitle(`${job.title} (Copy)`);
    }
    loadCompanyLogo();
  }, []);

  useFocusEffect(
    React.useCallback(() => {
      loadCompanyLogo();
    }, [])
  );

  const loadCompanyLogo = async () => {
    try {
      const cachedProfile = await AsyncStorage.getItem('company_profile');
      if (cachedProfile) {
        const profile = JSON.parse(cachedProfile);
        setCompanyLogo(profile.logo || null);
        if (profile.name || profile.company_name) setCompanyName(profile.name || profile.company_name);
      }
      const companyId = await AsyncStorage.getItem('company_id');
      if (companyId) {
        const response = await api.get(API_ENDPOINTS.companyProfile, {
          params: { company_id: companyId },
        });
        if (response.data?.success && response.data.data?.length > 0) {
          const latestProfile = response.data.data[0];
          setCompanyLogo(latestProfile.logo || null);
          if (latestProfile.name || latestProfile.company_name) setCompanyName(latestProfile.name || latestProfile.company_name);
          await AsyncStorage.setItem('company_profile', JSON.stringify(latestProfile));
        }
      }
    } catch (error) {
      console.warn('Error loading company logo:', error);
    }
  };

  const validateForm = () => {
    if (!jobTitle.trim()) {
      Alert.alert('Validation Error', 'Please enter a job title');
      return false;
    }
    if (!department.trim()) {
      Alert.alert('Validation Error', 'Please enter a department');
      return false;
    }
    if (!location.trim()) {
      Alert.alert('Validation Error', 'Please enter a location');
      return false;
    }
    if (!description.trim()) {
      Alert.alert('Validation Error', 'Please enter a job description');
      return false;
    }
    if (!salaryMin || !salaryMax) {
      Alert.alert('Validation Error', 'Please enter salary range');
      return false;
    }
    if (parseInt(salaryMin) > parseInt(salaryMax)) {
      Alert.alert('Validation Error', 'Minimum salary cannot be greater than maximum salary');
      return false;
    }
    return true;
  };

  const handleSave = async (saveStatus) => {
    if (!validateForm()) return;

    try {
      setIsSaving(true);
      const cid = await AsyncStorage.getItem('company_id') || '1';
      const token = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token');

      const jobData = {
        job_id: isEditMode ? (job?.job_id || 'JOB-' + Date.now().toString(36).toUpperCase()) : 'JOB-' + Date.now().toString(36).toUpperCase(),
        title: jobTitle,
        department,
        location,
        job_type: jobType,
        employment_type: jobType,
        experience_level: experienceLevel,
        description,
        responsibilities: responsibilities ? responsibilities.split('\n').filter(r => r.trim()) : [],
        qualifications: requirements ? requirements.split('\n').filter(r => r.trim()) : [],
        benefits: benefits ? JSON.stringify(benefits.split('\n').filter(b => b.trim())) : '[]',
        salary_min: parseInt(salaryMin) || 0,
        salary_max: parseInt(salaryMax) || 0,
        salary_currency: currency || 'GBP',
        application_deadline: deadline || null,
        positions_available: parseInt(positions) || 1,
        is_featured: isFeatured,
        is_active: saveStatus === 'active',
        status: saveStatus,
        required_skills: Array.isArray(requiredSkills) ? requiredSkills : (requiredSkills || '').split(',').map(s => s.trim()).filter(Boolean),
        company_id: parseInt(cid),
        posted_date: saveStatus === 'active' ? new Date().toISOString() : null,
        updated_at: new Date().toISOString(),
      };

      let savedId = isEditMode ? (job.id || job.job_id) : null;

      // ── POST/PUT to API ──
      try {
        const headers = { 'Content-Type': 'application/json', 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
        if (isEditMode && savedId) {
          // Update existing job
          const r = await fetch(`${API_BASE_URL}/api/data/jobs/${savedId}`, { method: 'PUT', headers, body: JSON.stringify(jobData) });
          const res = await r.json();
          console.log('✅ Job updated in DB:', res);
        } else {
          // Create new job
          const r = await fetch(`${API_BASE_URL}/api/data/jobs`, { method: 'POST', headers, body: JSON.stringify(jobData) });
          const res = await r.json();
          if (res?.data?.id) savedId = res.data.id;
          else if (res?.id) savedId = res.id;
          console.log('✅ Job created in DB:', res);
        }
      } catch (apiErr) {
        console.warn('API save failed (saving locally):', apiErr.message);
      }

      // ── Also save to AsyncStorage for offline access ──
      const localJob = {
        ...jobData,
        id: savedId || `job_${Date.now()}`,
        job_id: savedId || `job_${Date.now()}`,
        jobType, workMode, salaryMin: parseInt(salaryMin) || 0, salaryMax: parseInt(salaryMax) || 0,
        createdAt: jobData.created_at, updatedAt: jobData.updated_at,
        applicants: job?.applicants || 0, views: job?.views || 0,
      };

      const existingJobsJson = await AsyncStorage.getItem('jobs');
      const existingJobs = existingJobsJson ? JSON.parse(existingJobsJson) : [];
      let updatedJobs;
      if (isEditMode) {
        updatedJobs = existingJobs.map(j => (String(j.id) === String(savedId) || String(j.id) === String(job.id)) ? localJob : j);
      } else {
        updatedJobs = [localJob, ...existingJobs];
      }
      await AsyncStorage.setItem('jobs', JSON.stringify(updatedJobs));

      Alert.alert(
        'Success',
        `Job ${isEditMode ? 'updated' : 'created'} and saved to database!`,
        [{ text: 'OK', onPress: () => navigation.goBack() }]
      );

    } catch (error) {
      console.error('Error saving job:', error);
      Alert.alert('Error', 'Failed to save job. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const renderSection = (title, icon) => (
    <View style={styles.sectionHeader}>
      <MaterialCommunityIcons name={icon} size={20} color="#3B82F6" />
      <Text style={styles.sectionTitle}>{title}</Text>
    </View>
  );

  const renderInput = (label, value, onChangeText, placeholder, multiline = false, keyboardType = 'default') => (
    <View style={styles.inputContainer}>
      <Text style={styles.inputLabel}>{label}</Text>
      <TextInput
        style={[styles.input, multiline && styles.textArea]}
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor="#9CA3AF"
        multiline={multiline}
        numberOfLines={multiline ? 4 : 1}
        keyboardType={keyboardType}
      />
    </View>
  );

  const renderPicker = (label, value, onValueChange, options) => (
    <View style={styles.inputContainer}>
      <Text style={styles.inputLabel}>{label}</Text>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.pickerContainer}>
        {options.map((option) => (
          <TouchableOpacity
            key={option}
            style={[styles.pickerOption, value === option && styles.pickerOptionActive]}
            onPress={() => onValueChange(option)}
          >
            <Text style={[styles.pickerOptionText, value === option && styles.pickerOptionTextActive]}>
              {option}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  const renderSwitch = (label, value, onValueChange, description) => (
    <View style={styles.switchContainer}>
      <View style={styles.switchInfo}>
        <Text style={styles.switchLabel}>{label}</Text>
        {description && <Text style={styles.switchDescription}>{description}</Text>}
      </View>
      <Switch
        value={value}
        onValueChange={onValueChange}
        trackColor={{ false: '#D1D5DB', true: '#93C5FD' }}
        thumbColor={value ? '#3B82F6' : '#F3F4F6'}
      />
    </View>
  );

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size={36} color="#3B82F6" />
        <Text style={styles.loadingText}>Loading...</Text>
      </View>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#3B82F6" />
      
      {/* Header */}
      <LinearGradient
        colors={['#3B82F6', '#2563EB']}
        style={styles.header}
      >
        <View style={styles.headerContent}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <MaterialCommunityIcons name="arrow-left" size={24} color="#FFFFFF" />
          </TouchableOpacity>
          
          <View style={styles.headerInfo}>
            <View style={styles.headerTitleContainer}>
              {/* Company logo synced from CompanyProfileScreen */}
              <TouchableOpacity
                onPress={() => navigation.navigate('CompanyProfileScreen')}
                style={{ marginRight: 8 }}
              >
                {companyLogo ? (
                  <Image source={{ uri: companyLogo || undefined }} style={{ width: 30, height: 30, borderRadius: 15, resizeMode: 'cover' }} />
                ) : (
                  <MaterialCommunityIcons name="office-building" size={30} color="#fff" />
                )}
              </TouchableOpacity>
              <Text style={styles.headerTitle}>
                <Text>{isEditMode ? 'Edit Job' : isDuplicateMode ? 'Duplicate Job' : isTemplateMode ? 'Create from Template' : 'Create New Job'}</Text>
              </Text>
            </View>
            <Text style={styles.headerSubtitle}>
              {companyName || 'Fill in the details below'}
            </Text>
          </View>
          
          <TouchableOpacity
            style={styles.saveButton}
            onPress={() => handleSave('draft')}
            disabled={isSaving}
          >
            <MaterialCommunityIcons name="content-save" size={24} color="#FFFFFF" />
          </TouchableOpacity>
        </View>
      </LinearGradient>

      {/* Content */}
      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {/* Basic Information */}
        <View style={styles.section}>
          {renderSection('Basic Information', 'information')}
          
          {renderInput('Job Title *', jobTitle, setJobTitle, 'e.g. Senior Software Engineer')}
          {renderInput('Department *', department, setDepartment, 'e.g. Engineering')}
          {renderInput('Location *', location, setLocation, 'e.g. London, UK')}
          {renderPicker('Job Type', jobType, setJobType, jobTypes)}
          {renderPicker('Work Mode', workMode, setWorkMode, workModes)}
          {renderInput('Number of Positions', positions, setPositions, '1', false, 'numeric')}
          {renderPicker('Experience Level', experienceLevel, setExperienceLevel, experienceLevels)}
        </View>

        {/* Job Description */}
        <View style={styles.section}>
          {renderSection('Job Description', 'file-document-outline')}
          
          {renderInput('Description *', description, setDescription, 
            'Provide a detailed description of the role...', true)}
          {renderInput('Key Responsibilities', responsibilities, setResponsibilities, 
            'List the main responsibilities...', true)}
          {renderInput('Requirements', requirements, setRequirements, 
            'List the required skills and qualifications...', true)}
          {renderInput('Benefits', benefits, setBenefits, 
            'List the benefits and perks...', true)}
        </View>

        {/* Required Skills */}
        {requiredSkills && requiredSkills.length > 0 && (
          <View style={styles.section}>
            {renderSection('Required Skills', 'star')}
            <View style={styles.skillsContainer}>
              {requiredSkills.map((skill, index) => (
                <View key={index} style={styles.skillChip}>
                  <Text style={styles.skillText}>{skill}</Text>
                </View>
              ))}
            </View>
            <Text style={styles.helpText}>Skills from template (you can edit in requirements section)</Text>
          </View>
        )}

        {/* Compensation */}
        <View style={styles.section}>
          {renderSection('Compensation', 'cash-multiple')}
          
          <View style={styles.row}>
            <View style={[styles.inputContainer, { flex: 1, marginRight: 8 }]}>
              <Text style={styles.inputLabel}>Min Salary *</Text>
              <TextInput
                style={styles.input}
                value={salaryMin}
                onChangeText={setSalaryMin}
                placeholder="30000"
                placeholderTextColor="#9CA3AF"
                keyboardType="numeric"
              />
            </View>
            <View style={[styles.inputContainer, { flex: 1, marginLeft: 8 }]}>
              <Text style={styles.inputLabel}>Max Salary *</Text>
              <TextInput
                style={styles.input}
                value={salaryMax}
                onChangeText={setSalaryMax}
                placeholder="50000"
                placeholderTextColor="#9CA3AF"
                keyboardType="numeric"
              />
            </View>
          </View>
          
          {renderPicker('Currency', currency, setCurrency, currencies)}
        </View>

        {/* Settings */}
        <View style={styles.section}>
          {renderSection('Settings', 'cog')}
          
          {renderInput('Application Deadline', deadline, setDeadline, 
            'YYYY-MM-DD (optional)')}
          
          {renderSwitch('Featured Job', isFeatured, setIsFeatured, 
            'Highlight this job in search results')}
          
          {renderSwitch('AI-Powered Screening', aiPowered, setAiPowered, 
            'Use AI to automatically screen applicants')}
        </View>

        {/* Action Buttons */}
        <View style={styles.actionButtons}>
          <TouchableOpacity
            style={[styles.actionButton, styles.draftButton]}
            onPress={() => handleSave('draft')}
            disabled={isSaving}
          >
            {isSaving ? (
              <ActivityIndicator size={20} color="#6B7280" />
            ) : (
              <>
                <MaterialCommunityIcons name="content-save-outline" size={20} color="#6B7280" />
                <Text style={styles.draftButtonText}>Save as Draft</Text>
              </>
            )}
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.actionButton, styles.publishButton]}
            onPress={() => handleSave('active')}
            disabled={isSaving}
          >
            {isSaving ? (
              <ActivityIndicator size={20} color="#FFFFFF" />
            ) : (
              <>
                <MaterialCommunityIcons name="publish" size={20} color="#FFFFFF" />
                <Text style={styles.publishButtonText}>Publish Job</Text>
              </>
            )}
          </TouchableOpacity>
        </View>

        <View style={{ height: 40 }} />
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#3B82F6',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F9FAFB',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#6B7280',
  },
  header: {
    paddingTop: Platform.OS === 'ios' ? 0 : StatusBar.currentHeight,
    paddingBottom: 20,
    paddingHorizontal: 20,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerInfo: {
    flex: 1,
    marginLeft: 16,
  },
  headerTitleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginLeft: 12,
  },
  headerSubtitle: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.8)',
    marginTop: 4,
  },
  saveButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    flex: 1,
  },
  section: {
    backgroundColor: '#FFFFFF',
    marginTop: 16,
    marginHorizontal: 16,
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
    marginLeft: 8,
  },
  inputContainer: {
    marginBottom: 16,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
    marginBottom: 8,
  },
  input: {
    backgroundColor: '#F9FAFB',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    color: '#111827',
  },
  textArea: {
    height: 100,
    textAlignVertical: 'top',
  },
  pickerContainer: {
    flexDirection: 'row',
  },
  pickerOption: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: '#F3F4F6',
    marginRight: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  pickerOptionActive: {
    backgroundColor: '#3B82F6',
    borderColor: '#3B82F6',
  },
  pickerOptionText: {
    fontSize: 14,
    color: '#6B7280',
    fontWeight: '500',
  },
  pickerOptionTextActive: {
    color: '#FFFFFF',
  },
  switchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
  },
  switchInfo: {
    flex: 1,
    marginRight: 16,
  },
  switchLabel: {
    fontSize: 16,
    fontWeight: '500',
    color: '#111827',
  },
  switchDescription: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 4,
  },
  skillsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 8,
  },
  skillChip: {
    backgroundColor: '#EEF2FF',
    borderRadius: 20,
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  skillText: {
    fontSize: 12,
    color: '#3B82F6',
    fontWeight: '500',
  },
  helpText: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 8,
    fontStyle: 'italic',
  },
  row: {
    flexDirection: 'row',
  },
  actionButtons: {
    flexDirection: 'row',
    marginHorizontal: 16,
    marginTop: 24,
    gap: 12,
  },
  actionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    borderRadius: 12,
    gap: 8,
  },
  draftButton: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#D1D5DB',
  },
  draftButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#6B7280',
  },
  publishButton: {
    backgroundColor: '#3B82F6',
  },
  publishButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
});